import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { InternalGeneralContent } from '@ui/src';
import { useAppSelector } from '@store';
import { Balance } from '../types';
import { useLazyGetCustomersAccountBalanceQuery } from '../store/services/customers';

const ProblemInternalGeneralContentHOC = (): React.JSX.Element => {
  const {
    customersProblemTransactions: {
      internal: { transaction },
    },
    customersDictionaries: { currenciesList, overdraftEnabled },
  } = useAppSelector((state) => state);

  const { transactionId } = useParams();
  const [senderAccountBalance, setSenderAccountBalance] = useState<number>();
  const [recipientAccountBalance, setRecipientAccountBalance] =
    useState<number>();
  const [senderBalances, setSenderBalances] = useState<Balance[]>([]);
  const [recipientBalances, setRecipientBalances] = useState<Balance[]>([]);

  const [
    getSenderAccountBalance,
    { isLoading: senderBalanceIsLoading, isFetching: senderBalanceIsFetching },
  ] = useLazyGetCustomersAccountBalanceQuery();

  const [
    getRecipientAccountBalance,
    {
      isLoading: recipientBalanceIsLoading,
      isFetching: recipientBalanceIsFetching,
    },
  ] = useLazyGetCustomersAccountBalanceQuery();

  const getAccountsBalanceAction = useCallback(async () => {
    const senderResponse = (await getSenderAccountBalance({
      id: transaction?.senderAccount.id,
      currencyId: transaction?.currencyId as string,
    })) as { data: { data: { edges: { node: Balance }[] } } };

    if (senderResponse.data) {
      setSenderAccountBalance(senderResponse.data.data.edges[0].node.value);
      setSenderBalances(
        senderResponse.data.data.edges?.map((item) => item.node),
      );
    }

    const recipientResponse = (await getRecipientAccountBalance({
      id: transaction?.recipientAccount.id,
      currencyId: transaction?.currencyId as string,
    })) as { data: { data: { edges: { node: Balance }[] } } };

    if (recipientResponse.data) {
      setRecipientAccountBalance(
        recipientResponse.data.data.edges[0].node.value,
      );
      setRecipientBalances(
        recipientResponse.data.data.edges?.map((item) => item.node),
      );
    }
  }, [transaction]);

  useEffect(() => {
    if (transaction) {
      getAccountsBalanceAction();
    }
  }, [transaction]);

  return (
    <InternalGeneralContent
      transactionUrlType="internal"
      general={transaction}
      currenciesList={currenciesList}
      senderAccountBalance={senderAccountBalance}
      recipientAccountBalance={recipientAccountBalance}
      currencyId={transaction?.currencyId}
      senderAccountBalances={senderBalances}
      recipientAccountBalances={recipientBalances}
      senderAccountBalancesIsLoading={
        senderBalanceIsLoading || senderBalanceIsFetching
      }
      recipientAccountBalancesIsLoading={
        recipientBalanceIsLoading || recipientBalanceIsFetching
      }
      overdraftEnabled={overdraftEnabled}
      recipientAccountsWithInfo={[transaction?.recipientAccount]}
      senderAccountsWithInfo={[transaction?.senderAccount]}
      availableCurrencies={currenciesList?.map((item) => ({
        name: item.name,
        value: item.id,
      }))}
      allCurrenciesList={currenciesList?.map((item) => ({
        name: item.name,
        value: item.id,
      }))}
      currencyName={transaction?.currency?.name}
      transactionId={transactionId}
      withTransactionUrl
    />
  );
};

export default ProblemInternalGeneralContentHOC;
